/* eslint-disable @typescript-eslint/no-explicit-any */
import BucketIcon from '@svg/bucket_icon';

import { useEffect, useState } from 'react';
import { Loader } from './Loader/Loader';
import { DocumentLoader } from '@common/hooks/useDocument';
import { Upload, Queue, Embed } from '.';
import { DocumentEvent } from '@common/hooks/types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { State, bucketActions } from '@store/index';
import { SideBar } from '@common/SideBar';
import { Alert, AlertTitle } from '@mui/material';
import { getTask } from '@requests/buckets/buckets.requests';
import { Loader as TelnyxLoader } from '@common/hooks/useLoader';
import { LoaderConfiguration } from '@requests/loaders';
import { RequestError } from '@requests/common';
import { TelnyxBucket, TelnyxBucketFileEmbedding } from '@requests/buckets';

export type BucketFile = {
  embedded: boolean;
  last_modification: string;
  name: string;
  size: string;
};

export type QueueFile = {
  id: string;
  event: DocumentEvent;
  loading: boolean;
};

interface Props {
  documentLoader: DocumentLoader;
  files: BucketFile[];
  loaderSettingsOpen: string | null;
  setLoaderSettingsOpen: React.Dispatch<React.SetStateAction<string | null>>;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  setRemoveLoader: React.Dispatch<React.SetStateAction<string | null>>;
  isQueueFetching: boolean;
  fileStatus: 'success' | 'error' | 'pending';
  loader: LoaderConfiguration | undefined;
  loaderStatus: 'success' | 'error' | 'pending';
  isBucketLoading?: boolean;
  loaderError: RequestError | null;
}

export const BucketSidebar: React.FC<Props> = ({
  documentLoader,
  files,
  loaderSettingsOpen,
  setLoaderSettingsOpen,
  page,
  setPage,
  isQueueFetching,
  fileStatus,
  loader,
  loaderStatus,
  setRemoveLoader,
  isBucketLoading,
  loaderError,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const { documents, rowsLoading } = documentLoader;
  const { service_id, bucketName } = useParams();

  const bucketQueryKey = ['globalFiles', 'bucket', { service_id, bucket_name: bucketName }];
  const bucket = queryClient.getQueryData<TelnyxBucket>(bucketQueryKey);

  const task_id = useSelector((state: State) => state.buckets.task_id);

  const [tab, setTab] = useState('1');

  const embeddingQueryKey = ['globalFiles', 'embedding-files', { service_id, bucket_name: bucketName }];
  const embeddingFiles = queryClient.getQueryData<TelnyxBucketFileEmbedding[]>(embeddingQueryKey) || [];

  const isRefreshIntervalShort = !!task_id;
  const isLoading = loaderStatus === 'pending' || isBucketLoading;

  const { data: task, isLoading: isTaskLoading } = useQuery({
    queryKey: ['globalFiles', 'task', { task_id: task_id as string, service_id: service_id as string }],
    queryFn: getTask,
    staleTime: isRefreshIntervalShort ? 2000 : Infinity,
    enabled: Boolean(task_id),
    refetchInterval: isRefreshIntervalShort ? 2000 : Infinity,
  });

  useEffect(() => {
    if (tab === '1' && loader !== null && loaderStatus === 'success' && !isLoading) setTab('2');
  }, [tab, loader, loaderStatus, isLoading]);

  // remove the taskId if the task if finished or failed
  useEffect(() => {
    if (!task?.task_id) return;
    if (task.status === 'success' || task.status === 'failure') {
      dispatch(bucketActions.setTaskId(null));

      queryClient.invalidateQueries({ queryKey: ['globalFiles'] });
    }
  }, [task]);

  const isApiBucket = false;
  // const isApiBucket = spec?.bucket_name === bucketName;
  const isBucketEmbedded = bucket?.embedded;

  const embeddings: QueueFile[] = embeddingFiles
    .filter((file) => file?.status !== 'success' && file?.status !== 'failed')
    .map((document) => ({
      id: document.filename,
      event: DocumentEvent.Embed,
      loading: document.status === 'processing',
    }));

  const pending_embeddings: QueueFile[] = files
    .filter(
      (file) =>
        (bucket?.embedded || task_id || loader) && !embeddingFiles.some((document) => document.filename === file.name)
    )
    .map((document) => ({
      id: document.name,
      event: DocumentEvent.Embed,
      loading: false,
    }));

  const uploads: QueueFile[] = documents.map((document) => ({
    id: document?.name || document?.title,
    event: document?.event || DocumentEvent.Upload,
    loading: rowsLoading.includes(document.name),
  }));

  const queue = uploads
    .concat(pending_embeddings)
    .concat(embeddings)
    .sort((a, b) => {
      const aValue = Number(a.loading) || 0;
      const bValue = Number(b.loading) || 0;
      const loadingDifference = bValue - aValue;

      if (loadingDifference !== 0) return loadingDifference;
      else return a?.id?.localeCompare(b?.id);
    });

  const isLoaderDisabled = files.length > 0 && loader === null && page === 0;

  const tabs = [
    {
      label: 'Upload',
      value: '1',
      body: <Upload documentLoader={documentLoader} setTab={setTab} tab={tab} />,
      disabled: loader !== null || isLoading,
    },
    isLoaderDisabled && {
      label: 'Embed',
      value: '2',
      disabled: !isBucketEmbedded && (files?.length === 0 || loader !== null || fileStatus !== 'success' || isLoading),
      body: (
        <Embed
          documentLoader={documentLoader}
          setTab={setTab}
          files={files}
          task={task}
          setPage={setPage}
          isTaskLoading={isTaskLoading}
        />
      ),
    },
    !isLoaderDisabled && {
      label: 'Loader',
      value: '2',
      disabled: (isBucketEmbedded && loader === null) || (fileStatus !== 'success' && loader === null) || isLoading,
      body: !(isBucketEmbedded && loader === null) && (
        <Loader
          loader={loader}
          page={page}
          loaderStatus={loaderStatus}
          loaderSettingsOpen={loaderSettingsOpen}
          setLoaderSettingsOpen={setLoaderSettingsOpen}
          documentLoader={documentLoader}
          setTab={setTab}
          setRemoveLoader={setRemoveLoader}
        />
      ),
    },
    {
      label: 'Queue',
      value: '4',
      body: <Queue data={queue} loader={loader} setTab={setTab} files={files} isQueueFetching={isQueueFetching} />,
      disabled: isLoading,
    },
  ].filter(Boolean) as any[];

  if (isLoading || loaderError) {
    return (
      <SideBar
        tab={tab}
        setTab={setTab}
        tabs={tabs}
        replaceTabsWithNotice={
          loaderError ? (
            <Alert severity='error'>
              <AlertTitle>Loader Settings Error</AlertTitle>
              {loaderError.response?.data.error || loaderError?.message}
            </Alert>
          ) : (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                paddingTop: '1rem',
              }}
            >
              <TelnyxLoader />
            </div>
          )
        }
        onClose={() => navigate(`/${service_id}/buckets`)}
        title={bucketName as string}
        icon={<BucketIcon height={16} />}
        queueSize={0}
      />
    );
  }

  return (
    <SideBar
      tab={tab}
      setTab={setTab}
      tabs={tabs}
      replaceTabsWithNotice={
        isApiBucket && (
          <Alert severity='info' style={{ whiteSpace: 'pre-wrap' }}>
            <strong>Information</strong>
            {`\nThis bucket is being used by the Telnyx API loader integration. This integration will keep this bucket up to date with the latest endpoint information. There is one file for each endpoint we offer at Telnyx.\n\n`}
            <strong>Usage</strong>
            {`\nYou can utilize this bucket by adding the Telnyx API Search Extension to your AI Tool nodes. You can also search this bucket directly using the Bucket Search Extension.\n\n`}
            <strong>Problems</strong>
            {`\nIf you notice any issues with this bucket, please contact the NOC Engineering squad at\n`}
            <strong>#noc-eng-squad</strong>.
          </Alert>
        )
      }
      onClose={() => navigate(`/${service_id}/buckets`)}
      title={bucketName as string}
      // title={isApiBucket ? spec?.alias || spec?.bucket_name : (bucketName as string)}
      icon={<BucketIcon height={16} />}
      // icon={isApiBucket ? <TelnyxLogo height={16} color={theme.primary} /> : <BucketIcon height={16} />}
      queueSize={queue?.length || undefined}
    />
  );
};
